import { Link } from 'react-router-dom';
import { seoConfig } from '../../configs/seoConfig';
import { useSiteConfig } from '../../configs/siteConfig';
import LegalLayout, { LegalContact } from './LegalLayout';

const TermsPage = () => {
  const { companyName, owner, businessHours } = useSiteConfig().contactInfo;
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="Please read these terms carefully before using our services."
      seo={seoConfig.pages.terms}
    >
      <h2>1. Agreement to Terms</h2>
      <p>
        By accessing or using {companyName} ("the Website", "our services"), you
        agree to be bound by these Terms of Service and all applicable laws and
        regulations. If you do not agree with any of these terms, you are
        prohibited from using or accessing this site.
      </p>
      <p>
        This website is owned and operated by <strong>{owner.name}</strong>{' '}
        through <strong>{owner.website}</strong>.
      </p>

      <h2>2. Ownership and Intellectual Property</h2>
      <p>
        <strong>Website ownership:</strong> This website is owned by {owner.name}{' '}
        and operated under {owner.website}. All content, features, and
        functionality are owned by {owner.name} and are protected by
        international copyright, trademark, and other intellectual property laws.
      </p>
      <p>
        The digital products sold on this platform retain their respective
        ownership and licensing terms as specified in individual product
        descriptions. By purchasing a product, you receive a license to use it
        according to its specified terms, not ownership of the intellectual
        property itself.
      </p>

      <h2>3. Digital Products and Services</h2>
      <p>
        {companyName} provides a marketplace for digital products including but
        not limited to:
      </p>
      <ul>
        <li>Source code and software templates</li>
        <li>Website themes and templates</li>
        <li>Plugins and extensions</li>
        <li>Complete project solutions</li>
        <li>Digital documentation and resources</li>
      </ul>
      <p>
        All products are delivered digitally. Physical shipment is not
        applicable for any of our products.
      </p>

      <h2>4. User Accounts</h2>
      <p>To purchase products, you must create an account. You are responsible for:</p>
      <ul>
        <li>Maintaining the confidentiality of your account credentials</li>
        <li>All activities that occur under your account</li>
        <li>Providing accurate, current, and complete information</li>
        <li>Updating your information to keep it accurate and current</li>
        <li>Notifying us immediately of any unauthorized use of your account</li>
      </ul>
      <p>
        We reserve the right to suspend or terminate accounts that violate these
        terms or engage in fraudulent activities.
      </p>

      <h2>5. Order Processing and Delivery</h2>
      <div className="pk-alert" data-tone="info">
        <p>
          <strong>Processing time:</strong> After a successful payment, orders
          are typically processed within <strong>12 hours</strong>. During peak
          times, processing may take slightly longer.
        </p>
      </div>
      <p>
        Once your order is processed, the product's deliverables (access to its
        GitHub repository and/or downloadable files) are available from your
        order page. For GitHub repository access, the GitHub username on your
        account must be correct.
      </p>

      <h2>6. Payment Terms</h2>
      <p>
        All prices are listed in Indian Rupees (INR). Payment must be made in
        full before product delivery.
      </p>
      <p>
        Payment is made by <strong>UPI</strong>: at checkout you scan the QR
        code shown with any UPI app and then submit your UPI transaction ID.
        Each payment is verified manually before your order is processed.
      </p>
      <p>
        You pay from your own UPI app, so we never receive your UPI PIN, card
        details or bank login.
      </p>

      <h2>7. No Cancellation Policy</h2>
      <div className="pk-alert" data-tone="warning">
        <p>
          <strong>Important:</strong> Due to the digital nature of our products,
          we do <strong>not</strong> offer order cancellation once a purchase is
          completed. By completing a purchase, you acknowledge and accept this
          no-cancellation policy.
        </p>
        <p>
          Please review all product details, descriptions, and specifications
          carefully before making a purchase. If you have questions about a
          product, contact us before purchasing.
        </p>
      </div>

      <h2>8. No Refund Policy</h2>
      <div className="pk-alert" data-tone="warning">
        <p>
          <strong>Important:</strong> All sales are final. We do{' '}
          <strong>not</strong> provide refunds for any digital products purchased
          through our platform. This no-refund policy applies to all products
          without exception.
        </p>
        <p>
          Because digital products can be accessed and copied as soon as they are
          delivered, refunds are not available under any circumstances,
          including but not limited to:
        </p>
        <ul>
          <li>Change of mind</li>
          <li>Purchasing the wrong product</li>
          <li>Not reading product descriptions thoroughly</li>
          <li>
            Technical incompatibility (unless specified in the product
            description)
          </li>
          <li>Lack of technical skills to use the product</li>
        </ul>
        <p>
          We encourage you to carefully review product details and preview
          materials, and to contact us with any questions before making a
          purchase. See the{' '}
          <Link to="/refund">Refund Policy</Link> for details.
        </p>
      </div>

      <h2>9. Product Support and Updates</h2>
      <p>
        Support terms vary by product and are specified in individual product
        descriptions. Generally:
      </p>
      <ul>
        <li>
          Technical support is provided for a specified period as mentioned in
          the product details
        </li>
        <li>Product updates and bug fixes are provided when available</li>
        <li>
          Support does not include customization services unless explicitly
          stated
        </li>
        <li>
          We are not responsible for compatibility with third-party software or
          services
        </li>
      </ul>
      <p>
        Support is available {businessHours.days.replace(' - ', ' to ')},{' '}
        {businessHours.time.replace(' - ', ' to ')}.
      </p>

      <h2>10. License and Usage Rights</h2>
      <p>
        When you purchase a digital product, you receive a license to use it
        according to the terms specified in the product description. Unless
        otherwise stated:
      </p>
      <ul>
        <li>You may not resell, redistribute, or share the product</li>
        <li>You may not claim the product or its code as your own creation</li>
        <li>Usage is limited to the number of licenses purchased</li>
        <li>
          You must comply with any additional license terms provided with the
          product
        </li>
      </ul>
      <p>
        Violation of license terms may result in account termination and legal
        action.
      </p>

      <h2>11. Prohibited Activities</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the website or products for any illegal or unauthorized purpose</li>
        <li>Violate any laws in your jurisdiction</li>
        <li>
          Infringe upon or violate our intellectual property rights or the
          intellectual property rights of others
        </li>
        <li>Attempt to gain unauthorized access to our systems or networks</li>
        <li>Upload viruses or malicious code</li>
        <li>Engage in any automated use of the system</li>
        <li>Impersonate any person or entity</li>
        <li>Interfere with or disrupt the website or servers</li>
        <li>Share purchased products with unauthorized users</li>
      </ul>

      <h2>12. Disclaimers and Limitations of Liability</h2>
      <p>
        <strong>Disclaimer of warranties:</strong> Our products and services are
        provided "as is" and "as available" without any warranties of any kind,
        either express or implied. We do not warrant that:
      </p>
      <ul>
        <li>The products will meet your specific requirements</li>
        <li>The products will be uninterrupted, timely, secure, or error-free</li>
        <li>
          The results obtained from using the products will be accurate or
          reliable
        </li>
        <li>Any errors in the products will be corrected</li>
      </ul>
      <p>
        <strong>Limitation of liability:</strong> To the maximum extent permitted
        by law, {owner.name} and {owner.website} shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages, or any
        loss of profits or revenues, whether incurred directly or indirectly, or
        any loss of data, use, goodwill, or other intangible losses resulting
        from:
      </p>
      <ul>
        <li>Your use or inability to use our products or services</li>
        <li>Any unauthorized access to or use of our servers</li>
        <li>Any bugs, viruses, or other harmful code in our products</li>
        <li>Any errors or omissions in any content or product</li>
      </ul>

      <h2>13. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless {owner.name},{' '}
        {owner.website}, and our affiliates from any claims, damages,
        obligations, losses, liabilities, costs, or expenses arising from:
      </p>
      <ul>
        <li>Your use of our products or services</li>
        <li>Your violation of these Terms of Service</li>
        <li>Your violation of any third-party rights</li>
        <li>Your violation of any applicable laws or regulations</li>
      </ul>

      <h2>14. Modifications to Terms</h2>
      <p>
        We reserve the right to modify these terms at any time. Changes will be
        effective immediately upon posting to the website. Your continued use of
        the website after changes are posted constitutes your acceptance of the
        modified terms.
      </p>
      <p>
        We will update the "Last updated" date at the top of this page when
        changes are made. We encourage you to review these terms periodically.
      </p>

      <h2>15. Termination</h2>
      <p>
        We reserve the right to terminate or suspend your account and access to
        our services immediately, without prior notice or liability, for any
        reason, including but not limited to breach of these Terms of Service.
      </p>
      <p>
        Upon termination, your right to use the services will immediately cease.
        However, access to previously purchased products may be retained
        according to their individual license terms.
      </p>

      <h2>16. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the
        laws of India, without regard to its conflict of law provisions.
      </p>
      <p>
        Any disputes arising from these terms or your use of our services shall
        be subject to the exclusive jurisdiction of the courts in India.
      </p>

      <h2>17. Severability</h2>
      <p>
        If any provision of these Terms is found to be unenforceable or invalid,
        that provision will be limited or eliminated to the minimum extent
        necessary, and the remaining provisions will remain in full force and
        effect.
      </p>

      <h2>18. Contact Information</h2>
      <p>If you have any questions about these Terms of Service, contact us:</p>
      <LegalContact />

      <h2>19. Entire Agreement</h2>
      <p>
        These Terms of Service, together with our{' '}
        <Link to="/privacy">Privacy Policy</Link>, our{' '}
        <Link to="/refund">Refund Policy</Link> and any other legal notices
        published on this website, constitute the entire agreement between you
        and {companyName} concerning your use of the website and services.
      </p>

      <p className="lg-accept">
        <strong>
          By using our website and services, you acknowledge that you have read,
          understood, and agree to be bound by these Terms of Service.
        </strong>
      </p>
    </LegalLayout>
  );
};

export default TermsPage;
